// src/components/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { motion } from "framer-motion";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/button.tsx
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-brand-gold text-brand-ink hover:bg-brand-gold/90 gold-glow",
        destructive: "bg-red-600 text-white hover:bg-red-700",
        outline: "border border-brand-gold/30 bg-transparent text-brand-gold hover:bg-brand-gold/10 hover:border-brand-gold",
        secondary: "bg-brand-cigar text-brand-ivory hover:bg-brand-cigar/80",
        ghost: "hover:bg-brand-gold/10 hover:text-brand-gold",
        link: "text-brand-gold underline-offset-4 hover:underline",
        premium: "bg-gradient-to-r from-brand-gold via-brand-gold/90 to-brand-gold text-brand-ink hover:from-brand-gold/90 hover:to-brand-gold/80 gold-glow"
      },
      size: {
        default: "h-11 px-4 py-2 min-h-[44px]",
        sm: "h-10 rounded-md px-3 min-h-[40px]",
        lg: "h-12 rounded-md px-8 min-h-[48px]",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px]"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    if (asChild) {
      return /* @__PURE__ */ jsx(
        Slot,
        {
          className: cn(buttonVariants({ variant, size, className })),
          ref,
          ...props
        }
      );
    }
    return /* @__PURE__ */ jsx(
      motion.button,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        whileHover: { scale: 1.02 },
        whileTap: { scale: 0.98 },
        transition: { duration: 0.15 },
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/card.tsx
import * as React2 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Card = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm texture-noise premium-border",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "h3",
  {
    ref,
    className: cn(
      "text-2xl font-serif font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";

// src/components/dialog.tsx
import * as React3 from "react";
import { Fragment, jsx as jsx3, jsxs } from "react/jsx-runtime";
var Dialog = ({
  open,
  onOpenChange,
  children
}) => {
  return /* @__PURE__ */ jsx3(Fragment, { children: open && /* @__PURE__ */ jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center", children: [
    /* @__PURE__ */ jsx3(
      "div",
      {
        className: "fixed inset-0 bg-black/80 backdrop-blur-sm",
        onClick: () => onOpenChange?.(false)
      }
    ),
    children
  ] }) });
};
Dialog.displayName = "Dialog";
var DialogContent = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "div",
  {
    ref,
    className: cn(
      "relative z-50 grid w-full min-w-[320px] sm:min-w-[400px] max-w-lg gap-4 border bg-background p-6 shadow-lg",
      "rounded-lg texture-noise premium-border",
      "animate-in fade-in-0 zoom-in-95",
      className
    ),
    ...props
  }
));
DialogContent.displayName = "DialogContent";
var DialogHeader = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "div",
  {
    ref,
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
));
DialogHeader.displayName = "DialogHeader";
var DialogTitle = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "h2",
  {
    ref,
    className: cn(
      "text-lg font-serif font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = "DialogTitle";
var DialogDescription = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = "DialogDescription";
var DialogFooter = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "div",
  {
    ref,
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
));
DialogFooter.displayName = "DialogFooter";

// src/components/input.tsx
import * as React4 from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Input = React4.forwardRef(
  ({ className, type, style, ...props }, ref) => {
    return /* @__PURE__ */ jsx4(
      "input",
      {
        type,
        className: cn(
          "flex h-11 min-h-[44px] w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all hover:border-brand-gold/50 [color-scheme:dark]",
          className
        ),
        style: {
          colorScheme: "dark",
          color: "rgb(244, 241, 233) !important",
          WebkitTextFillColor: "rgb(244, 241, 233)",
          ...style
        },
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/components/label.tsx
import * as React5 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx5 } from "react/jsx-runtime";
var labelVariants = cva2(
  "text-sm font-medium leading-none font-sans text-brand-ivory peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx5(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;

// src/components/phone-input.tsx
import * as React6 from "react";
import PhoneInputWithCountry from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { jsx as jsx6 } from "react/jsx-runtime";
var CustomInput = React6.forwardRef(({ className, error, ...props }, ref) => /* @__PURE__ */ jsx6(
  "input",
  {
    ...props,
    ref,
    className: cn(
      "flex h-11 min-h-[44px] w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all hover:border-brand-gold/50 [color-scheme:dark]",
      error && "border-red-500 focus-visible:ring-red-500",
      className
    ),
    style: {
      colorScheme: "dark",
      color: "rgb(244, 241, 233)",
      WebkitTextFillColor: "rgb(244, 241, 233)"
    }
  }
));
CustomInput.displayName = "CustomInput";
var PhoneInput = React6.forwardRef(
  ({ className, onChange, error, defaultCountry = "BR", value, ...props }, _ref) => {
    return /* @__PURE__ */ jsx6(
      PhoneInputWithCountry,
      {
        international: true,
        countryCallingCodeEditable: false,
        defaultCountry,
        value,
        onChange: (newValue) => onChange?.(newValue ?? void 0),
        inputComponent: CustomInput,
        className: cn("phone-input-container", className),
        ...props
      }
    );
  }
);
PhoneInput.displayName = "PhoneInput";

// src/components/popover.tsx
import * as React7 from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var PopoverContext = React7.createContext(void 0);
var usePopoverContext = () => {
  const context = React7.useContext(PopoverContext);
  if (!context) {
    throw new Error("Popover components must be used within a Popover");
  }
  return context;
};
var Popover = ({ open: controlledOpen, onOpenChange: controlledOnOpenChange, children }) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React7.useState(false);
  const triggerRef = React7.useRef(null);
  const open = controlledOpen !== void 0 ? controlledOpen : uncontrolledOpen;
  const onOpenChange = controlledOnOpenChange || setUncontrolledOpen;
  React7.useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && triggerRef.current && !triggerRef.current.contains(event.target) && !event.target.closest("[data-popover-content]")) {
        onOpenChange(false);
      }
    };
    const handleEscape = (event) => {
      if (open && event.key === "Escape") {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onOpenChange]);
  return /* @__PURE__ */ jsx7(PopoverContext.Provider, { value: { open, onOpenChange, triggerRef }, children: /* @__PURE__ */ jsx7("div", { className: "relative inline-block", children }) });
};
Popover.displayName = "Popover";
var PopoverTrigger = React7.forwardRef(
  ({ asChild, children, onClick, ...props }, ref) => {
    const { open, onOpenChange, triggerRef } = usePopoverContext();
    const handleClick = (e) => {
      onOpenChange(!open);
      onClick?.(e);
    };
    const mergedRef = (node) => {
      triggerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };
    if (asChild && React7.isValidElement(children)) {
      return React7.cloneElement(children, {
        ref: mergedRef,
        onClick: handleClick,
        ...props
      });
    }
    return /* @__PURE__ */ jsx7("button", { ref: mergedRef, onClick: handleClick, ...props, children });
  }
);
PopoverTrigger.displayName = "PopoverTrigger";
var PopoverContent = React7.forwardRef(
  ({ className, align = "center", sideOffset = 4, ...props }, ref) => {
    const { open } = usePopoverContext();
    if (!open) return null;
    const alignmentClasses = {
      start: "left-0",
      center: "left-1/2 -translate-x-1/2",
      end: "right-0"
    };
    return /* @__PURE__ */ jsx7(
      "div",
      {
        ref,
        "data-popover-content": true,
        className: cn(
          "absolute z-50 top-full mt-2",
          alignmentClasses[align],
          "rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none",
          "animate-in fade-in-0 zoom-in-95",
          className
        ),
        style: { marginTop: `${sideOffset}px` },
        ...props
      }
    );
  }
);
PopoverContent.displayName = "PopoverContent";

// src/components/separator.tsx
import * as React8 from "react";
import { jsx as jsx8 } from "react/jsx-runtime";
var Separator = React8.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx8(
    "div",
    {
      ref,
      role: decorative ? "none" : "separator",
      "aria-orientation": orientation,
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator.displayName = "Separator";

// src/components/toast.tsx
import * as React9 from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva as cva3 } from "class-variance-authority";
import { X } from "lucide-react";
import { jsx as jsx9 } from "react/jsx-runtime";
var ToastProvider = ToastPrimitives.Provider;
var ToastViewport = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  ToastPrimitives.Viewport,
  {
    ref,
    className: cn(
      "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    ),
    ...props
  }
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
var toastVariants = cva3(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all font-sans font-normal data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        destructive: "destructive group border-destructive bg-destructive text-destructive-foreground font-sans",
        success: "border-brand-gold/30 bg-brand-gold/10 text-brand-gold"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Toast = React9.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx9(
    ToastPrimitives.Root,
    {
      ref,
      className: cn(toastVariants({ variant }), className),
      ...props
    }
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;
var ToastAction = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  ToastPrimitives.Action,
  {
    ref,
    className: cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    ),
    ...props
  }
));
ToastAction.displayName = ToastPrimitives.Action.displayName;
var ToastClose = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  ToastPrimitives.Close,
  {
    ref,
    className: cn(
      "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    ),
    "toast-close": "",
    ...props,
    children: /* @__PURE__ */ jsx9(X, { className: "h-4 w-4" })
  }
));
ToastClose.displayName = ToastPrimitives.Close.displayName;
var ToastTitle = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  ToastPrimitives.Title,
  {
    ref,
    className: cn("toast-title", className),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
var ToastDescription = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  ToastPrimitives.Description,
  {
    ref,
    className: cn("toast-description", className),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

// src/hooks/use-toast.ts
import * as React10 from "react";
var TOAST_LIMIT = 1;
var TOAST_REMOVE_DELAY = 1e6;
var count = 0;
function genId() {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}
var toastTimeouts = /* @__PURE__ */ new Map();
var addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
var reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
var listeners = [];
var memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}
function toast({ ...props }) {
  const id = genId();
  console.log(">>> TOAST CALLED with props:", props, "id:", id);
  console.log(">>> Current listeners count:", listeners.length);
  console.log(">>> Current toasts in memory:", memoryState.toasts);
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  console.log(">>> After dispatch, toasts:", memoryState.toasts);
  return {
    id,
    dismiss,
    update
  };
}
function useToast() {
  const [state, setState] = React10.useState(memoryState);
  React10.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
}

// src/components/toaster.tsx
import { jsx as jsx10, jsxs as jsxs2 } from "react/jsx-runtime";
var titleStyle = {
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px"
};
var descriptionStyle = {
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  opacity: 0.9
};
function Toaster() {
  const { toasts } = useToast();
  return /* @__PURE__ */ jsxs2(ToastProvider, { children: [
    toasts.map(function({ id, title, description, action, ...props }) {
      return /* @__PURE__ */ jsxs2(Toast, { ...props, children: [
        /* @__PURE__ */ jsxs2("div", { className: "grid gap-1", children: [
          title && /* @__PURE__ */ jsx10("div", { style: titleStyle, children: title }),
          description && /* @__PURE__ */ jsx10("div", { style: descriptionStyle, children: description })
        ] }),
        action,
        /* @__PURE__ */ jsx10(ToastClose, {})
      ] }, id);
    }),
    /* @__PURE__ */ jsx10(ToastViewport, {})
  ] });
}

// src/index.ts
var styles = "./styles/globals.css";
export {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PhoneInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  Toaster,
  buttonVariants,
  cn,
  reducer,
  styles,
  toast,
  useToast
};
//# sourceMappingURL=index.mjs.map